/**
 * TimelineItem Component
 *
 * Renders a single event in the operator progress timeline.
 * Supports three event types: assessment, training, and achievement.
 *
 * Visual design:
 * - Vertical left border line connecting items
 * - Colored dot per type: assessment=indigo, training=violet, achievement=Guild Green
 * - Date badge with smart year display (omit year for current year)
 * - Type-specific content layout
 * - Manager recognition note in Guild Mint card with star icon
 */

import {
  ClipboardList,
  CheckCircle2,
  Award,
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
  Sparkles,
  Brain,
  Gamepad2,
  MessageCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { TimelineEvent } from '../hooks/useOperatorProgress';

interface TimelineItemProps {
  item: TimelineEvent;
  /** First item in the list — no connecting line above */
  isFirst: boolean;
  /** Last item in the list — no connecting line below */
  isLast: boolean;
  /** Callback when an assessment item is tapped (opens detail sheet) */
  onSelect?: (item: TimelineEvent) => void;
}

// ============================================
// DATE FORMATTING
// ============================================

/** Format date: "MMM d" for current year, "MMM d, yyyy" for previous years */
function formatTimelineDate(dateStr: string): string {
  const date = new Date(dateStr);
  const currentYear = new Date().getFullYear();
  if (date.getFullYear() === currentYear) {
    return format(date, 'MMM d');
  }
  return format(date, 'MMM d, yyyy');
}

// ============================================
// DOT COLORS
// ============================================

function getDotColor(type: TimelineEvent['type']): string {
  switch (type) {
    case 'assessment':
      return 'bg-indigo-500';
    case 'training':
      return 'bg-violet-500';
    case 'achievement':
      return 'bg-guild-green';
    case 'quiz_completed':
      return 'bg-cyan-500';
    case 'sequencer_completed':
      return 'bg-rose-500';
    default:
      return 'bg-gray-400';
  }
}

// ============================================
// TYPE ICONS
// ============================================

function getTypeIcon(type: TimelineEvent['type']) {
  switch (type) {
    case 'assessment':
      return <ClipboardList size={14} strokeWidth={1.5} />;
    case 'training':
      return <CheckCircle2 size={14} strokeWidth={1.5} />;
    case 'achievement':
      return <Award size={14} strokeWidth={1.5} />;
    case 'quiz_completed':
      return <Brain size={14} strokeWidth={1.5} />;
    case 'sequencer_completed':
      return <Gamepad2 size={14} strokeWidth={1.5} />;
    default:
      return null;
  }
}

function getTypeLabel(type: TimelineEvent['type']): string {
  switch (type) {
    case 'assessment':
      return 'Assessment';
    case 'training':
      return 'Training Completed';
    case 'achievement':
      return 'Achievement';
    case 'quiz_completed':
      return 'Quiz Completed';
    case 'sequencer_completed':
      return 'Sequencer Completed';
    default:
      return '';
  }
}

// ============================================
// TREND INDICATOR
// ============================================

function TimelineTrend({ trend }: { trend: TimelineEvent['trend'] }) {
  if (!trend) return null;

  switch (trend) {
    case 'improving':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
          <TrendingUp size={13} strokeWidth={1.5} />
          Improving
        </span>
      );
    case 'declining':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
          <TrendingDown size={13} strokeWidth={1.5} />
          Declining
        </span>
      );
    case 'steady':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-op-text-tertiary">
          <Minus size={13} strokeWidth={1.5} />
          Steady
        </span>
      );
    case 'first':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-500">
          <Sparkles size={13} strokeWidth={1.5} />
          First assessment
        </span>
      );
    default:
      return null;
  }
}

// ============================================
// ASSESSMENT CONTENT
// ============================================

function AssessmentContent({ item }: { item: TimelineEvent }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-1.5">
      {/* Zone + SOP name */}
      {(item.zoneName || item.sopTitle) && (
        <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
          {item.zoneName && item.sopTitle
            ? `${item.zoneName} \u2014 ${item.sopTitle}`
            : item.sopTitle ?? item.zoneName}
        </p>
      )}

      {/* Task focus */}
      {item.taskFocus && (
        <p className="text-xs text-op-text-secondary">
          Focus: {item.taskFocus}
        </p>
      )}

      {/* AI summary (first 2 sentences, truncated server-side) */}
      {item.aiSummary && (
        <p className="text-xs leading-relaxed text-op-text-secondary line-clamp-3">
          {item.aiSummary}
        </p>
      )}

      {/* Trend indicator */}
      {item.trend && <TimelineTrend trend={item.trend} />}

      {/* Manager recognition note */}
      {item.managerNote && (
        <div className="mt-2 rounded-xl bg-guild-mint/20 p-3">
          <div className="flex items-start gap-2">
            <Star
              size={14}
              strokeWidth={1.5}
              className="mt-0.5 flex-shrink-0 text-amber-500"
              fill="currentColor"
            />
            <div className="min-w-0 flex-1">
              <p className="text-xs leading-relaxed text-op-text-primary">
                &ldquo;{item.managerNote}&rdquo;
              </p>
              {item.managerName && (
                <p className="mt-1 text-[11px] font-medium text-op-text-tertiary">
                  &mdash; {item.managerName}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review with coach link — uses <span role="button"> to avoid
          nesting <button> inside the outer assessment card <button> */}
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigate('/operator/coach?mode=debrief');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            navigate('/operator/coach?mode=debrief');
          }
        }}
        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-guild-green cursor-pointer"
      >
        <MessageCircle size={12} strokeWidth={1.5} />
        Review with coach
      </span>
    </div>
  );
}

// ============================================
// TRAINING CONTENT
// ============================================

function TrainingContent({ item }: { item: TimelineEvent }) {
  return (
    <div className="space-y-1">
      {item.materialTitle && (
        <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
          {item.materialTitle}
        </p>
      )}
      {item.sopName && (
        <p className="text-xs text-op-text-secondary line-clamp-1">
          {item.sopName}
        </p>
      )}
      <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-600">
        <CheckCircle2 size={13} strokeWidth={1.5} />
        Completed
      </span>
    </div>
  );
}

// ============================================
// PRACTICE CONTENT (quiz_completed / sequencer_completed)
// ============================================

function PracticeContent({ item }: { item: TimelineEvent }) {
  const isQuiz = item.type === 'quiz_completed';
  const Icon = isQuiz ? Brain : Gamepad2;
  const iconColor = isQuiz ? 'text-cyan-600' : 'text-rose-600';
  const label = isQuiz ? 'Quiz' : 'Step Sequencer';

  return (
    <div className="space-y-1">
      {/* SOP + section/task title */}
      {(item.sopTitle || item.taskFocus) && (
        <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
          {item.taskFocus ?? item.sopTitle}
        </p>
      )}
      {item.sopTitle && item.taskFocus && (
        <p className="text-xs text-op-text-secondary line-clamp-1">
          {item.sopTitle}
        </p>
      )}
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${iconColor}`}>
          <Icon size={13} strokeWidth={1.5} />
          {label}
        </span>
        {item.practiceScore && (
          <span className="text-xs font-medium text-op-text-secondary">
            {item.practiceScore}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================
// ACHIEVEMENT CONTENT
// ============================================

function AchievementContent({ item }: { item: TimelineEvent }) {
  return (
    <div className="flex items-center gap-2">
      <Award size={16} strokeWidth={1.5} className="flex-shrink-0 text-guild-green" />
      <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
        {item.achievementTitle ?? 'Achievement unlocked'}
      </p>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function TimelineItem({ item, isFirst, isLast, onSelect }: TimelineItemProps) {
  const isTappable = item.type === 'assessment' && !!onSelect;

  const handleTap = () => {
    if (isTappable && onSelect) {
      onSelect(item);
    }
  };

  return (
    <div className="relative flex gap-3">
      {/* ---- Left column: dot + vertical line ---- */}
      <div className="flex flex-col items-center">
        {/* Top connecting line (hidden for first item) */}
        <div
          className={`w-0.5 flex-shrink-0 ${isFirst ? 'bg-transparent' : 'bg-gray-200'}`}
          style={{ height: 12 }}
        />

        {/* Colored dot */}
        <div
          className={`h-3 w-3 flex-shrink-0 rounded-full ring-2 ring-white ${getDotColor(item.type)}`}
        />

        {/* Bottom connecting line (hidden for last item) */}
        <div
          className={`w-0.5 flex-1 ${isLast ? 'bg-transparent' : 'bg-gray-200'}`}
        />
      </div>

      {/* ---- Right column: content card ---- */}
      <div className="flex-1 pb-4">
        {/* Date + type label */}
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs font-medium text-op-text-tertiary">
            {formatTimelineDate(item.date)}
          </span>
          <span className="flex items-center gap-1 text-[11px] font-medium text-op-text-tertiary">
            {getTypeIcon(item.type)}
            {getTypeLabel(item.type)}
          </span>
        </div>

        {/* Type-specific content — tappable for assessments */}
        {isTappable ? (
          <button
            type="button"
            onClick={handleTap}
            className="w-full rounded-xl bg-op-card p-3 text-left ring-1 ring-black/[0.04] transition-transform duration-150 active:scale-[0.98] cursor-pointer focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <AssessmentContent item={item} />
          </button>
        ) : (
          <div className="rounded-xl bg-op-card p-3 ring-1 ring-black/[0.04]">
            {item.type === 'assessment' && <AssessmentContent item={item} />}
            {item.type === 'training' && <TrainingContent item={item} />}
            {item.type === 'achievement' && <AchievementContent item={item} />}
            {(item.type === 'quiz_completed' || item.type === 'sequencer_completed') && (
              <PracticeContent item={item} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
